import { useState, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { UserContext } from './UserContext';

export default function useAuth() {
  const navigate = useNavigate();
  let location = useLocation();
  const { user, setUser } = useContext(UserContext);
  const [error, setError] = useState(null);

  const getUserContext = async () => {
    return user;
  };

  // set user context and handle navigation
  /*
  note: this function and getUserContext() should probably be removed.
  setUserContext()'s logic (GET profile then navigate) is only needed in
  loginUser(). after that, user/profile sync/updates should be handled with the
  useUpdateUserProfile() hook. navigation logic should be completely separate.
  see more comments in useUpdateUserProfile.js
  */
  const setUserContext = async (redirectUrl = '/') => {
    try {
      const axiosInstance = axios.create({
        baseURL: `${process.env.REACT_APP_FEATURED_API}auth/profile/`,
        headers: {
          Authorization: `JWT ${localStorage.getItem('token')}`,
        },
      });

      const { data } = await axiosInstance.get('/');
      setUser(data.currentUser);

      if (!data.currentUser.name || !data.currentUser.news_organization) {
        navigate('/profile');
      } else if (redirectUrl !== 'no') {
        navigate(redirectUrl);
      }
	  
    } catch (error) {
      console.error('Error setting user context:', error);
      setError('Failed to load user profile');
    }
  };

  // register user (admins only)
  const registerUser = async (data) => {
    const registrationResponse = {
      success: false,
      data: 'failure'
    }
    try {
      const axiosInstance = axios.create({
        baseURL: `${process.env.REACT_APP_FEATURED_API}auth/user/`,
        headers: {
          Authorization: `JWT ${localStorage.getItem('token')}`,
        },
      });

      const requestData = {
        email: data.email,
      };

      if (data.name) {
        requestData.name = data.name;
      }
      if (data.org) {
        requestData.org = data.org;
      }

      const { data: response } = await axiosInstance.post('signup/', requestData);

      registrationResponse.success = true;
      registrationResponse.data = response.user;
      return registrationResponse;
    } catch (error) {
      registrationResponse.success = false;
      registrationResponse.data = error.response?.data?.errors;
      console.error(error);
      return registrationResponse;
    }
  };

  // login user
  const loginUser = async (credential, clientId) => {
    try {
      const axiosInstance = axios.create({
        baseURL: `${process.env.REACT_APP_FEATURED_API}auth/user/`,
      });

      // Validate Google OAuth token
      const verifyResponse = await axiosInstance.post('verifytoken/', {
        credential,
        clientId,
      });

      if (verifyResponse.status !== 200) {
        throw new Error('OAuth verification failed');
      }

      // Sign in with verified email
      const signinResponse = await axiosInstance.post('signin/', {
        email: verifyResponse.data.email,
      });

      // Store token and handle redirect
      localStorage.setItem('token', signinResponse.data.token);
      const { from } = location.state || { from: { pathname: '/' } };
      await setUserContext(from.pathname);

      return true;
    } catch (error) {
      formatErrors(error);
      return false;
    }
  };

  // check if current user is superuser (admins only)
  const check_superuser = async () => {
    try {
      const axiosInstance = axios.create({
        baseURL: `${process.env.REACT_APP_FEATURED_API}auth/user/`,
        headers: {
          Authorization: `JWT ${localStorage.getItem('token')}`,
        },
      });

      const { data } = await axiosInstance.get('check-superuser/');
      return data.is_superuser;
    } catch (error) {
      return false;
    }
  };

  // check if current user is admin
  const check_admin = async () => {
    try {
      const axiosInstance = axios.create({
        baseURL: `${process.env.REACT_APP_FEATURED_API}auth/user/`,
        headers: {
          Authorization: `JWT ${localStorage.getItem('token')}`,
        },
      });

      const { data } = await axiosInstance.get('checkadmin/');
      return data.is_staff;
    } catch (error) {
      return false;
    }
  };

  const toTitleCase = (str) => {
    return str.replace(
      /\w\S*/g,
      (txt) => txt.charAt(0).toUpperCase() + txt.slice(1).toLowerCase()
    );
  };

  const formatErrors = (err) => {
    try {
      if (!err.response?.data) {
        throw new Error('Invalid error response');
      }

      const messages = Object.entries(err.response.data)
        .map(([key, errors]) => {
          if (!Array.isArray(errors)) return '';
          const prefix =
            key !== 'non_field_errors'
              ? `${toTitleCase(key.replace(/_/g, ' '))} : `
              : '';
          return errors.map((error) => `${prefix}${error}`).join('<br>');
        })
        .filter(Boolean)
        .join('<br>');

      const errorElement = document.getElementById('form-error');
      if (errorElement) {
        errorElement.innerHTML = messages || 'An error occurred';
      }
    } catch (error) {
      console.error('Error formatting messages:', error);
      const errorElement = document.getElementById('form-error');
      if (errorElement) {
        errorElement.innerHTML = 'Something went wrong';
      }
    }
  };

  return {
    getUserContext,
    setUserContext,
    registerUser,
    loginUser,
    check_superuser,
    check_admin,
    error,
  };
}
